import { useState, useEffect, useCallback } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { api } from "../../lib/api";
import { translateStatus, translateRisk } from "../../lib/constants";

// Category labels and colors
const CATEGORY_CONFIG = {
  competitor: { label: "Concorrente", color: "bg-red-100 text-red-700 dark:bg-red-900/30 dark:text-red-400", icon: "🎯" },
  influencer: { label: "Influencer", color: "bg-purple-100 text-purple-700 dark:bg-purple-900/30 dark:text-purple-400", icon: "📢" },
  ipss: { label: "IPSS", color: "bg-green-100 text-green-700 dark:bg-green-900/30 dark:text-green-400", icon: "🤝" },
  other: { label: "Outro", color: "bg-gray-100 text-gray-700 dark:bg-gray-800 dark:text-gray-400", icon: "📋" }
};

// Other Entity Detail Page Component
export const OtherDetailPage = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const [entity, setEntity] = useState(null);
  const [loading, setLoading] = useState(true);
  const [conflicts, setConflicts] = useState([]);
  const [showEditModal, setShowEditModal] = useState(false);
  const [editData, setEditData] = useState({});
  const [saving, setSaving] = useState(false);
  const [enriching, setEnriching] = useState(false);

  const fetchEntity = useCallback(async () => {
    try {
      const response = await api.get(`/others/${id}`);
      setEntity(response.data);
      setEditData(response.data);
    } catch (error) {
      console.error('Error fetching entity:', error);
      toast.error('Erro ao carregar entidade');
      navigate('/others');
    } finally {
      setLoading(false);
    }
  }, [id, navigate]);

  const fetchConflicts = useCallback(async () => {
    try {
      const response = await api.get('/conflicts');
      const entityConflicts = response.data.filter(
        c => c.entity1_id === id || c.entity2_id === id
      );
      setConflicts(entityConflicts);
    } catch (error) {
      console.error('Error fetching conflicts:', error);
    }
  }, [id]);

  useEffect(() => {
    fetchEntity();
    fetchConflicts();
  }, [fetchEntity, fetchConflicts]);

  const handleUpdate = async () => {
    setSaving(true);
    try {
      await api.put(`/others/${id}`, editData);
      toast.success('Entidade atualizada com sucesso');
      setShowEditModal(false);
      fetchEntity();
    } catch (error) {
      console.error('Error updating entity:', error);
      toast.error('Erro ao atualizar entidade');
    } finally {
      setSaving(false);
    }
  };

  const handleEnrich = async () => {
    setEnriching(true);
    try {
      const response = await api.post(`/others/${id}/enrich`);
      toast.success('Dados enriquecidos com sucesso');
      fetchEntity();
    } catch (error) {
      console.error('Error enriching entity:', error);
      toast.error('Erro ao enriquecer dados');
    } finally {
      setEnriching(false);
    }
  };

  const handleDelete = async () => {
    if (!window.confirm('Tem a certeza que deseja eliminar esta entidade? Esta ação não pode ser revertida.')) {
      return;
    }
    try {
      await api.delete(`/others/${id}`);
      toast.success('Entidade eliminada com sucesso');
      navigate('/others');
    } catch (error) {
      console.error('Error deleting entity:', error);
      toast.error('Erro ao eliminar entidade');
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div>
      </div>
    );
  }

  if (!entity) {
    return (
      <div className="text-center py-12">
        <p className="text-muted-foreground">Entidade não encontrada</p>
        <button onClick={() => navigate('/others')} className="mt-4 text-primary hover:underline">
          Voltar à lista
        </button>
      </div>
    );
  }

  const categoryConfig = CATEGORY_CONFIG[entity.category] || CATEGORY_CONFIG.other;

  return (
    <div data-testid="other-detail-page" className="space-y-6 animate-fade-in">
      {/* Header */}
      <div className="flex flex-col md:flex-row md:items-start justify-between gap-4">
        <div>
          <button
            onClick={() => navigate('/others')}
            className="text-sm text-muted-foreground hover:text-foreground flex items-center gap-1 mb-2"
          >
            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
            </svg>
            Voltar
          </button>
          <div className="flex items-center gap-3">
            <span className="text-3xl">{categoryConfig.icon}</span>
            <div>
              <h1 className="text-2xl md:text-3xl font-bold font-['Plus_Jakarta_Sans']">{entity.name}</h1>
              <div className="flex items-center gap-2 mt-1">
                <span className={`px-2 py-0.5 text-xs rounded-full font-medium ${categoryConfig.color}`}>
                  {categoryConfig.label}
                </span>
                <span className={`status-badge status-badge-${entity.status || 'active'}`}>
                  {translateStatus(entity.status || 'active')}
                </span>
                <span className={`status-badge status-badge-${entity.risk_level || 'low'}`}>
                  {translateRisk(entity.risk_level || 'low')}
                </span>
              </div>
            </div>
          </div>
        </div>
        <div className="flex gap-2">
          <button
            onClick={handleEnrich}
            disabled={enriching}
            className="px-4 py-2 rounded-lg border border-border font-medium hover:bg-muted transition-colors flex items-center gap-2"
          >
            {enriching ? (
              <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-primary"></div>
            ) : (
              <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 10V3L4 14h7v7l9-11h-7z" />
              </svg>
            )}
            Enriquecer
          </button>
          <button
            onClick={() => setShowEditModal(true)}
            className="px-4 py-2 rounded-lg bg-primary text-primary-foreground font-medium hover:bg-primary/90 transition-colors flex items-center gap-2"
            data-testid="edit-entity-btn"
          >
            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z" />
            </svg>
            Editar
          </button>
          <button
            onClick={handleDelete}
            className="px-4 py-2 rounded-lg bg-red-500 text-white font-medium hover:bg-red-600 transition-colors"
            data-testid="delete-entity-btn"
          >
            Eliminar
          </button>
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Main Info */}
        <div className="lg:col-span-2 space-y-6">
          {/* Description */}
          {entity.description && (
            <div className="rounded-xl border bg-card p-6">
              <h2 className="text-lg font-semibold mb-3">Descrição</h2>
              <p className="text-muted-foreground">{entity.description}</p>
            </div>
          )}

          {/* General Info */}
          <div className="rounded-xl border bg-card p-6">
            <h2 className="text-lg font-semibold mb-4">Informações Gerais</h2>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <p className="text-sm text-muted-foreground">Nome Legal</p>
                <p className="font-medium">{entity.legal_name || entity.name}</p>
              </div>
              <div>
                <p className="text-sm text-muted-foreground">NIF</p>
                <p className="font-medium">{entity.tax_id || '-'}</p>
              </div>
              <div>
                <p className="text-sm text-muted-foreground">NIF IVA</p>
                <p className="font-medium">{entity.vat_number || '-'}</p>
              </div>
              <div>
                <p className="text-sm text-muted-foreground">RCBE</p>
                <p className="font-medium">{entity.rcbe_code || '-'}</p>
              </div>
              <div>
                <p className="text-sm text-muted-foreground">Tipo de Relação</p>
                <p className="font-medium">{entity.relationship_type || '-'}</p>
              </div>
              <div>
                <p className="text-sm text-muted-foreground">Indústria</p>
                <p className="font-medium">{entity.industry || '-'}</p>
              </div>
              <div>
                <p className="text-sm text-muted-foreground">CAE</p>
                <p className="font-medium">{entity.cae_code || '-'}</p>
              </div>
              <div>
                <p className="text-sm text-muted-foreground">Website</p>
                {entity.website ? (
                  <a href={entity.website} target="_blank" rel="noopener noreferrer" className="text-primary hover:underline">
                    {entity.website}
                  </a>
                ) : (
                  <p className="font-medium">-</p>
                )}
              </div>
            </div>
          </div>

          {/* Address */}
          <div className="rounded-xl border bg-card p-6">
            <h2 className="text-lg font-semibold mb-4">Morada</h2>
            <div className="grid grid-cols-2 gap-4">
              <div className="col-span-2">
                <p className="text-sm text-muted-foreground">Endereço</p>
                <p className="font-medium">{entity.address || '-'}</p>
              </div>
              <div>
                <p className="text-sm text-muted-foreground">Código Postal</p>
                <p className="font-medium">{entity.postal_code || '-'}</p>
              </div>
              <div>
                <p className="text-sm text-muted-foreground">Cidade</p>
                <p className="font-medium">{entity.city || '-'}</p>
              </div>
              <div>
                <p className="text-sm text-muted-foreground">País</p>
                <p className="font-medium">{entity.country || 'Portugal'}</p>
              </div>
            </div>
          </div>

          {/* Contact */}
          <div className="rounded-xl border bg-card p-6">
            <h2 className="text-lg font-semibold mb-4">Contacto Principal</h2>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <p className="text-sm text-muted-foreground">Nome</p>
                <p className="font-medium">{entity.contact_name || '-'}</p>
              </div>
              <div>
                <p className="text-sm text-muted-foreground">Cargo</p>
                <p className="font-medium">{entity.contact_position || '-'}</p>
              </div>
              <div>
                <p className="text-sm text-muted-foreground">Email</p>
                {entity.contact_email ? (
                  <a href={`mailto:${entity.contact_email}`} className="text-primary hover:underline">
                    {entity.contact_email}
                  </a>
                ) : (
                  <p className="font-medium">-</p>
                )}
              </div>
              <div>
                <p className="text-sm text-muted-foreground">Telefone</p>
                <p className="font-medium">{entity.contact_phone || '-'}</p>
              </div>
            </div>
          </div>

          {/* Shareholders */}
          <ShareholdersSection entity={entity} entityId={id} onUpdate={fetchEntity} />
        </div>

        {/* Sidebar */}
        <div className="space-y-6">
          {/* Risk Score */}
          <div className="rounded-xl border bg-card p-6">
            <h2 className="text-lg font-semibold mb-4">Score de Risco</h2>
            <div className="flex items-center gap-4">
              <div className="relative w-20 h-20">
                <svg className="w-20 h-20 -rotate-90">
                  <circle cx="40" cy="40" r="36" fill="none" stroke="currentColor" strokeWidth="8" className="text-muted/20" />
                  <circle 
                    cx="40" cy="40" r="36" fill="none" strokeWidth="8"
                    className={entity.risk_score > 0.6 ? 'text-red-500' : entity.risk_score > 0.3 ? 'text-amber-500' : 'text-green-500'}
                    strokeDasharray={`${(entity.risk_score || 0) * 226} 226`}
                    strokeLinecap="round"
                  />
                </svg>
                <div className="absolute inset-0 flex items-center justify-center">
                  <span className="text-lg font-bold">{Math.round((entity.risk_score || 0) * 100)}%</span>
                </div>
              </div>
              <div>
                <p className={`font-semibold ${entity.risk_score > 0.6 ? 'text-red-500' : entity.risk_score > 0.3 ? 'text-amber-500' : 'text-green-500'}`}>
                  {entity.risk_score > 0.6 ? 'Alto Risco' : entity.risk_score > 0.3 ? 'Risco Médio' : 'Baixo Risco'}
                </p>
                <p className="text-sm text-muted-foreground">Baseado na análise de conflitos</p>
              </div>
            </div>
          </div>

          {/* Conflicts */}
          <div className="rounded-xl border bg-card p-6">
            <h2 className="text-lg font-semibold mb-4">Conflitos Detetados</h2>
            {conflicts.length === 0 ? (
              <p className="text-muted-foreground text-center py-4">Nenhum conflito detetado</p>
            ) : (
              <div className="space-y-2">
                {conflicts.map((conflict) => (
                  <div key={conflict.conflict_id} className="p-3 rounded-lg bg-muted/50">
                    <div className="flex items-center gap-2 mb-1">
                      <span className={`w-2 h-2 rounded-full ${conflict.severity === 'critical' ? 'bg-red-500' : conflict.severity === 'high' ? 'bg-amber-500' : 'bg-yellow-500'}`}></span>
                      <span className="font-medium text-sm">{conflict.conflict_type}</span>
                    </div>
                    <p className="text-xs text-muted-foreground">{conflict.relationship}</p>
                  </div>
                ))}
              </div>
            )}
          </div>

          {/* Notes */}
          {entity.notes && (
            <div className="rounded-xl border bg-card p-6">
              <h2 className="text-lg font-semibold mb-3">Notas</h2>
              <p className="text-sm text-muted-foreground">{entity.notes}</p>
            </div>
          )}

          {/* Metadata */}
          <div className="rounded-xl border bg-card p-6">
            <h2 className="text-lg font-semibold mb-4">Metadados</h2>
            <div className="space-y-2 text-sm">
              <div className="flex justify-between">
                <span className="text-muted-foreground">Criado em</span>
                <span>{entity.created_at ? new Date(entity.created_at).toLocaleDateString('pt-PT') : '-'}</span>
              </div>
              <div className="flex justify-between">
                <span className="text-muted-foreground">Atualizado em</span>
                <span>{entity.updated_at ? new Date(entity.updated_at).toLocaleDateString('pt-PT') : '-'}</span>
              </div>
              {entity.enrichment_date && (
                <div className="flex justify-between">
                  <span className="text-muted-foreground">Enriquecido em</span>
                  <span>{new Date(entity.enrichment_date).toLocaleDateString('pt-PT')}</span>
                </div>
              )}
            </div>
          </div>
        </div>
      </div>

      {/* Edit Modal */}
      {showEditModal && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4" onClick={() => setShowEditModal(false)}>
          <div className="bg-card rounded-xl border border-border w-full max-w-2xl max-h-[90vh] overflow-y-auto" onClick={(e) => e.stopPropagation()}>
            <div className="p-6 border-b border-border">
              <h3 className="text-lg font-semibold">Editar Entidade KYU</h3>
            </div>
            <div className="p-6 space-y-4">
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium mb-1">Nome *</label>
                  <input
                    type="text"
                    value={editData.name || ''}
                    onChange={(e) => setEditData({ ...editData, name: e.target.value })}
                    className="w-full px-3 py-2 rounded-lg border border-input bg-background"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium mb-1">Categoria</label>
                  <select
                    value={editData.category || 'other'}
                    onChange={(e) => setEditData({ ...editData, category: e.target.value })}
                    className="w-full px-3 py-2 rounded-lg border border-input bg-background"
                  >
                    <option value="competitor">Concorrente</option>
                    <option value="influencer">Influencer</option>
                    <option value="ipss">IPSS</option>
                    <option value="other">Outro</option>
                  </select>
                </div>
                <div>
                  <label className="block text-sm font-medium mb-1">NIF</label>
                  <input
                    type="text"
                    value={editData.tax_id || ''}
                    onChange={(e) => setEditData({ ...editData, tax_id: e.target.value })}
                    className="w-full px-3 py-2 rounded-lg border border-input bg-background"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium mb-1">Tipo de Relação</label>
                  <input
                    type="text"
                    value={editData.relationship_type || ''}
                    onChange={(e) => setEditData({ ...editData, relationship_type: e.target.value })}
                    className="w-full px-3 py-2 rounded-lg border border-input bg-background"
                  />
                </div>
              </div>
              <div>
                <label className="block text-sm font-medium mb-1">Descrição</label>
                <textarea
                  value={editData.description || ''}
                  onChange={(e) => setEditData({ ...editData, description: e.target.value })}
                  className="w-full px-3 py-2 rounded-lg border border-input bg-background min-h-[80px]"
                />
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium mb-1">Morada</label>
                  <input
                    type="text"
                    value={editData.address || ''}
                    onChange={(e) => setEditData({ ...editData, address: e.target.value })}
                    className="w-full px-3 py-2 rounded-lg border border-input bg-background"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium mb-1">Cidade</label>
                  <input
                    type="text"
                    value={editData.city || ''}
                    onChange={(e) => setEditData({ ...editData, city: e.target.value })}
                    className="w-full px-3 py-2 rounded-lg border border-input bg-background"
                  />
                </div>
              </div>
              <div>
                <label className="block text-sm font-medium mb-1">Notas</label>
                <textarea
                  value={editData.notes || ''}
                  onChange={(e) => setEditData({ ...editData, notes: e.target.value })}
                  className="w-full px-3 py-2 rounded-lg border border-input bg-background min-h-[60px]"
                />
              </div>
              <div className="flex gap-3 pt-4">
                <button
                  onClick={() => setShowEditModal(false)}
                  className="flex-1 px-4 py-2 rounded-lg border border-border font-medium hover:bg-muted transition-colors"
                >
                  Cancelar
                </button>
                <button
                  onClick={handleUpdate}
                  disabled={!editData.name || saving}
                  className="flex-1 px-4 py-2 rounded-lg bg-primary text-primary-foreground font-medium hover:bg-primary/90 transition-colors disabled:opacity-50"
                  data-testid="save-entity-btn"
                >
                  {saving ? 'A guardar...' : 'Guardar'}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

// Shareholders Section Component
const ShareholdersSection = ({ entity, entityId, onUpdate }) => {
  const [showAddModal, setShowAddModal] = useState(false);
  const [showEditModal, setShowEditModal] = useState(false);
  const [editingIndex, setEditingIndex] = useState(null);
  const [newShareholder, setNewShareholder] = useState({ name: '', tax_id: '', role: '', percentage: '', rcbe_code: '' });
  const [editShareholder, setEditShareholder] = useState({ name: '', tax_id: '', role: '', percentage: '', rcbe_code: '' });
  const [saving, setSaving] = useState(false);

  const shareholders = entity.shareholders || [];

  const handleAdd = async () => {
    setSaving(true);
    try {
      const updatedShareholders = [...shareholders, newShareholder];
      await api.put(`/others/${entityId}`, { shareholders: updatedShareholders });
      toast.success('Acionista adicionado');
      setShowAddModal(false);
      setNewShareholder({ name: '', tax_id: '', role: '', percentage: '', rcbe_code: '' });
      onUpdate();
    } catch (error) {
      console.error('Error adding shareholder:', error);
      toast.error('Erro ao adicionar acionista');
    } finally {
      setSaving(false);
    }
  };

  const handleEdit = (index) => {
    setEditingIndex(index);
    setEditShareholder({ ...shareholders[index] });
    setShowEditModal(true);
  };

  const handleSaveEdit = async () => {
    setSaving(true);
    try {
      const updatedShareholders = [...shareholders];
      updatedShareholders[editingIndex] = editShareholder;
      await api.put(`/others/${entityId}`, { shareholders: updatedShareholders });
      toast.success('Acionista atualizado');
      setShowEditModal(false);
      setEditingIndex(null);
      onUpdate();
    } catch (error) {
      console.error('Error updating shareholder:', error);
      toast.error('Erro ao atualizar acionista');
    } finally {
      setSaving(false);
    }
  };

  const handleRemove = async (index) => {
    if (!window.confirm('Tem a certeza que deseja remover este acionista?')) return;
    try {
      const updatedShareholders = shareholders.filter((_, i) => i !== index);
      await api.put(`/others/${entityId}`, { shareholders: updatedShareholders });
      toast.success('Acionista removido');
      onUpdate();
    } catch (error) {
      console.error('Error removing shareholder:', error);
      toast.error('Erro ao remover acionista');
    }
  };

  const renderForm = (data, setData) => (
    <div className="space-y-4">
      <div>
        <label className="block text-sm font-medium mb-1">Nome *</label>
        <input
          type="text"
          value={data.name || ''}
          onChange={(e) => setData({ ...data, name: e.target.value })}
          className="w-full px-3 py-2 rounded-lg border border-input bg-background"
        />
      </div>
      <div>
        <label className="block text-sm font-medium mb-1">NIF</label>
        <input
          type="text"
          value={data.tax_id || ''}
          onChange={(e) => setData({ ...data, tax_id: e.target.value })}
          className="w-full px-3 py-2 rounded-lg border border-input bg-background"
        />
      </div>
      <div>
        <label className="block text-sm font-medium mb-1">Cargo/Função</label>
        <input
          type="text"
          value={data.role || ''}
          onChange={(e) => setData({ ...data, role: e.target.value })}
          className="w-full px-3 py-2 rounded-lg border border-input bg-background"
        />
      </div>
      <div>
        <label className="block text-sm font-medium mb-1">Percentagem (%)</label>
        <input
          type="number"
          min="0"
          max="100"
          value={data.percentage || ''}
          onChange={(e) => setData({ ...data, percentage: e.target.value })}
          className="w-full px-3 py-2 rounded-lg border border-input bg-background"
        />
      </div>
      <div>
        <label className="block text-sm font-medium mb-1">Código RCBE</label>
        <input
          type="text"
          value={data.rcbe_code || ''}
          onChange={(e) => setData({ ...data, rcbe_code: e.target.value })}
          className="w-full px-3 py-2 rounded-lg border border-input bg-background"
        />
      </div>
    </div>
  );

  return (
    <div className="rounded-xl border bg-card p-6">
      <div className="flex items-center justify-between mb-4">
        <h2 className="text-lg font-semibold">Pessoas Chave / Acionistas</h2>
        <button
          onClick={() => setShowAddModal(true)}
          className="px-3 py-1.5 rounded-lg bg-primary text-primary-foreground text-sm font-medium hover:bg-primary/90 transition-colors flex items-center gap-1"
          data-testid="add-shareholder-btn"
        >
          <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
          </svg>
          Adicionar
        </button>
      </div>

      {shareholders.length === 0 ? (
        <p className="text-muted-foreground text-center py-4">Nenhum acionista registado</p>
      ) : (
        <div className="space-y-2">
          {shareholders.map((sh, idx) => (
            <div key={idx} className="flex items-center justify-between p-3 rounded-lg bg-muted/50 hover:bg-muted transition-colors">
              <div className="flex-1">
                <p className="font-medium">{sh.name}</p>
                <p className="text-sm text-muted-foreground">
                  {sh.role || 'Acionista'} {sh.percentage && `• ${sh.percentage}%`}
                  {sh.tax_id && <span className="ml-2">• NIF: {sh.tax_id}</span>}
                  {sh.rcbe_code && <span className="ml-2">• RCBE: {sh.rcbe_code}</span>}
                </p>
              </div>
              <div className="flex items-center gap-1">
                <button
                  onClick={() => handleEdit(idx)}
                  className="p-1.5 text-blue-500 hover:bg-blue-100 dark:hover:bg-blue-900/30 rounded"
                  title="Editar"
                >
                  <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z" />
                  </svg>
                </button>
                <button
                  onClick={() => handleRemove(idx)}
                  className="p-1.5 text-red-500 hover:bg-red-100 dark:hover:bg-red-900/30 rounded"
                  title="Remover"
                >
                  <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                  </svg>
                </button>
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Add Modal */}
      {showAddModal && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4" onClick={() => setShowAddModal(false)}>
          <div className="bg-card rounded-xl border border-border w-full max-w-md max-h-[90vh] overflow-y-auto" onClick={(e) => e.stopPropagation()}>
            <div className="p-6 border-b border-border">
              <h3 className="text-lg font-semibold">Adicionar Acionista</h3>
            </div>
            <div className="p-6">
              {renderForm(newShareholder, setNewShareholder)}
              <div className="flex gap-3 pt-6">
                <button onClick={() => setShowAddModal(false)} className="flex-1 px-4 py-2 rounded-lg border border-border font-medium hover:bg-muted transition-colors">
                  Cancelar
                </button>
                <button
                  onClick={handleAdd}
                  disabled={!newShareholder.name || saving}
                  className="flex-1 px-4 py-2 rounded-lg bg-primary text-primary-foreground font-medium hover:bg-primary/90 transition-colors disabled:opacity-50"
                >
                  {saving ? 'A guardar...' : 'Guardar'}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Edit Modal */}
      {showEditModal && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4" onClick={() => setShowEditModal(false)}>
          <div className="bg-card rounded-xl border border-border w-full max-w-md max-h-[90vh] overflow-y-auto" onClick={(e) => e.stopPropagation()}>
            <div className="p-6 border-b border-border">
              <h3 className="text-lg font-semibold">Editar Acionista</h3>
            </div>
            <div className="p-6">
              {renderForm(editShareholder, setEditShareholder)}
              <div className="flex gap-3 pt-6">
                <button onClick={() => setShowEditModal(false)} className="flex-1 px-4 py-2 rounded-lg border border-border font-medium hover:bg-muted transition-colors">
                  Cancelar
                </button>
                <button
                  onClick={handleSaveEdit}
                  disabled={!editShareholder.name || saving}
                  className="flex-1 px-4 py-2 rounded-lg bg-primary text-primary-foreground font-medium hover:bg-primary/90 transition-colors disabled:opacity-50"
                >
                  {saving ? 'A guardar...' : 'Atualizar'}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default OtherDetailPage;
